import {
    TextField, Button, Box, Typography, Link, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow,
    Dialog, DialogContent, IconButton, Stack, Toolbar
} from "@mui/material";

import { useState, useEffect, useRef } from "react";
import { Close, Description, Info } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { importPayrollSetup } from "../../../api";
import XLSX from "xlsx-js-style";
import SampleTemplateFile from "../../../files/Payroll setup import.xls";
import PayrollsPopup from "../../../Components/PayrollPopup";
import { useFetchCorporations } from "../../../Hooks";
import { Dropdown } from "../../../HelperFunctions/PayrollDropdown";
import { useParams } from "react-router-dom";
import { Loader } from "../../../Components/Loader";
import { payrollSetupStyles } from "../../../styles/payrollSetup.styles";

// -----------------------------------------

const PayrollImport = ({ open, onClose, selectedCorp: propSelectedCorp, onSuccess }) => {

    const { corpId } = useParams();
    const { enqueueSnackbar } = useSnackbar();

    const [selectedCorp, setSelectedCorp] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileObj, setFileObj] = useState(null);
    const [errorRows, setErrorRows] = useState([]);
    const [actionLoading, setActionLoading] = useState(false);
    const hasInitializedRef = useRef(false);

    const isLoading = actionLoading;

    // --- CONFIRMATION STATE ---
    const [alertPopup, setAlertPopup] = useState({
        open: false,
        type: "",
        message: "",
        onConfirm: null
    });

    const { corporations, loading: corpLoading } = useFetchCorporations(null, { includeSelect: true });

    useEffect(() => {
        if (!open) {
            hasInitializedRef.current = false;
            setSelectedFile(null);
            setFileObj(null);
            setErrorRows([]);
            setSelectedCorp(null);
            return;
        }

        if (hasInitializedRef.current) return;

        if (corporations && corporations.length > 0) {
            const propId = propSelectedCorp?.ID || propSelectedCorp?.corpValue || propSelectedCorp?.value;
            const searchParams = new URLSearchParams(window.location.search);
            const urlCorpId = corpId || searchParams.get("corpid") || searchParams.get("corpId") || searchParams.get("corpselect");
            const targetId = (propId && propId !== "000000000000000000000000000000000000") ? propId : urlCorpId;

            if (targetId) {
                const matched = corporations.find(c => {
                    const cId = c?.ID || c?.corpValue || c?.value;
                    return String(cId).toLowerCase() === String(targetId).toLowerCase();
                });
                if (matched) {
                    setSelectedCorp(matched);
                } else if (propSelectedCorp) {
                    setSelectedCorp(propSelectedCorp);
                }
            } else {
                setSelectedCorp(corporations[0] || null);
            }
            hasInitializedRef.current = true;
        }
    }, [open, corporations, propSelectedCorp, corpId]);

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Extract file extension with a more robust approach
        const allowedExtensions = [".xlsx", ".csv", ".xls"];
        const fileName = file.name.trim().toLowerCase();
        const fileExtension = fileName.substring(fileName.lastIndexOf("."));

        // Check if the extension is valid
        if (!allowedExtensions.includes(fileExtension)) {
            //alert("Unsupported file type. Please upload a valid .xls, .xlsx, or .csv file.");
            setAlertPopup({
                open: true,
                type: "error",
                message: "Unsupported file type. Please upload a valid .xls, .xlsx, or .csv file.",
                onConfirm: () => setAlertPopup({ ...alertPopup, open: false })
            });
            event.target.value = "";  // Reset the file input
            setSelectedFile(null);  // Clear the selected file
            return;
        }

        if (file.size === 0) {
            setAlertPopup({
                open: true,
                type: "error",
                message: "The uploaded file is empty. Please check and upload a valid file.",
                onConfirm: () => setAlertPopup(prev => ({ ...prev, open: false }))
            });
            event.target.value = "";
            setSelectedFile(null);
            setFileObj(null);
            return;
        }

        setSelectedFile(file.name);
        setFileObj(file);  // Set the file object
    };


    const exportErrorsToCSV = () => {
        if (errorRows.length === 0) return;

        // Create a new worksheet from the error rows array
        const ws = XLSX.utils.json_to_sheet(errorRows);

        // Auto-size columns
        const keys = Object.keys(errorRows[0] || {});
        ws["!cols"] = keys.map(key => {
            const maxLength = Math.max(
                key.length,
                ...errorRows.map(r => r[key] ? r[key].toString().length : 0)
            );
            return { wch: Math.min(maxLength + 3, 50) };
        });

        // Header styling
        const range = XLSX.utils.decode_range(ws["!ref"]);
        for (let col = range.s.c; col <= range.e.c; col++) {
            const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
            if (ws[cellAddress]) {
                ws[cellAddress].s = {
                    font: { name: "Poppins", bold: true, sz: 11, color: { rgb: "000000" } },
                    fill: { fgColor: { rgb: "F5F5F5" } },
                    alignment: { horizontal: "center", vertical: "center" }
                };
            }
        }

        // Body cells styling
        for (let row = 1; row <= range.e.r; row++) {
            for (let col = range.s.c; col <= range.e.c; col++) {
                const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
                if (ws[cellAddress]) {
                    ws[cellAddress].s = {
                        font: { name: "Poppins", sz: 11 },
                        alignment: { vertical: "center" }
                    };
                }
            }
        }

        // Create a new workbook and append the worksheet
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Failed Records");

        // Write the workbook and trigger a download
        XLSX.writeFile(wb, "FailedPayrollImportRecords.xlsx");
    };

    // Helper function to safely get column values from row object
    const getColVal = (row, colName) => {
        if (!row || typeof row !== "object") return "";
        const key = Object.keys(row).find(k => k.trim().toLowerCase() === colName.toLowerCase());
        return key ? row[key] : "";
    };

    // Helper function to extract failed list from backend response
    const getFailedList = (data) => {
        if (!data || typeof data !== "object") return null;
        return data.FailedCorps || data.failedCorps || null;
    };

    // Helper function to check if a row contains a value (case-insensitive)
    const rowContainsValue = (row, val) => {
        if (!row || typeof row !== "object" || !val) return false;
        const target = String(val).trim().toLowerCase();
        if (!target) return false;
        return Object.values(row).some(cell => {
            const cellStr = String(cell ?? "").trim().toLowerCase();
            return cellStr === target || cellStr.includes(target);
        });
    };

    // Helper function to parse error status text into structured row errors matching original excel rows
    const parseErrorStatus = (errorText, excelRows = [], importPayrollItemsMap = new Map()) => {
        if (!errorText) return [];

        let rawLines = [];
        if (Array.isArray(errorText)) {
            rawLines = errorText;
        } else if (typeof errorText === "string") {
            if (errorText.includes("\n")) {
                rawLines = errorText.split(/\r?\n/);
            } else if (/Row\s+\d+:/i.test(errorText)) {
                rawLines = errorText.split(/(?=\bRow\s+\d+:)/i);
            } else {
                rawLines = [errorText];
            }
        }

        const errorLines = [];
        rawLines.forEach(l => {
            const str = String(l || "").trim();
            if (!str) return;
            if (str.includes("\n")) {
                str.split(/\r?\n/).forEach(sub => {
                    const trimmed = sub.trim();
                    if (trimmed) errorLines.push(trimmed);
                });
            } else {
                errorLines.push(str);
            }
        });

        if (errorLines.length === 0) return [];

        const baseRowTemplate = excelRows.length > 0 ? { ...excelRows[0] } : {};
        Object.keys(baseRowTemplate).forEach(k => { baseRowTemplate[k] = ""; });

        return errorLines.map(line => {
            // Extract row number: 'Row 1', 'Row 2', etc.
            const rowMatch = line.match(/\bRow\s+(\d+)\b/i);
            const rowNum = rowMatch ? parseInt(rowMatch[1], 10) : null;

            // Extract all quoted values: 'Regular', 'Managerr', 'F & B Department'
            const quoteMatches = [...line.matchAll(/'([^']+)'/g)].map(m => m[1].trim());

            let matchedRow = null;

            // 1. If rowNum is present, check excelRows candidates
            if (rowNum !== null) {
                const candidate1 = excelRows[rowNum - 1]; // 1-based data row index (excelRows[0] = Row 1)
                const candidate2 = excelRows[rowNum - 2]; // In case header was counted as Row 1
                const candidate3 = excelRows[rowNum];     // 0-based fallback

                if (candidate1 && quoteMatches.length > 0 && quoteMatches.some(q => rowContainsValue(candidate1, q))) {
                    matchedRow = candidate1;
                } else if (candidate2 && quoteMatches.length > 0 && quoteMatches.some(q => rowContainsValue(candidate2, q))) {
                    matchedRow = candidate2;
                } else if (candidate3 && quoteMatches.length > 0 && quoteMatches.some(q => rowContainsValue(candidate3, q))) {
                    matchedRow = candidate3;
                } else if (candidate1) {
                    matchedRow = candidate1;
                } else if (candidate2) {
                    matchedRow = candidate2;
                }
            }

            // 2. If matchedRow not found yet, try finding by field values in quotes
            if (!matchedRow && quoteMatches.length > 0) {
                for (const qVal of quoteMatches) {
                    if (!qVal) continue;
                    const qLower = qVal.toLowerCase();

                    if (/payroll\s*item|item\s*name/i.test(line)) {
                        matchedRow = excelRows.find(r => String(getColVal(r, "Item Name")).trim().toLowerCase() === qLower);
                    } else if (/job\s*title/i.test(line)) {
                        matchedRow = excelRows.find(r => String(getColVal(r, "Job Title")).trim().toLowerCase() === qLower);
                    } else if (/department/i.test(line)) {
                        matchedRow = excelRows.find(r => String(getColVal(r, "Department")).trim().toLowerCase() === qLower);
                    } else if (/expense/i.test(line)) {
                        matchedRow = excelRows.find(r => String(getColVal(r, "Expense Account")).trim().toLowerCase() === qLower);
                    } else if (/payable/i.test(line)) {
                        matchedRow = excelRows.find(r => String(getColVal(r, "Payable Account")).trim().toLowerCase() === qLower);
                    }

                    if (!matchedRow) {
                        matchedRow = excelRows.find(r => rowContainsValue(r, qVal));
                    }

                    if (matchedRow) break;
                }
            }

            // 3. Extract field values from matchedRow or fallbacks
            let itemType = "-";
            let itemName = "-";
            let department = "-";
            let jobTitle = "-";
            let wageType = "";
            let payType = "";
            let expenseAccount = "";
            let payableAccount = "";
            let originalRow = { ...baseRowTemplate };

            if (matchedRow) {
                originalRow = { ...matchedRow };
                itemType = String(getColVal(matchedRow, "Item Type") || "").trim() || "-";
                itemName = String(getColVal(matchedRow, "Item Name") || "").trim() || "-";
                department = String(getColVal(matchedRow, "Department") || "").trim() || "-";
                jobTitle = String(getColVal(matchedRow, "Job Title") || "").trim() || "-";
                wageType = String(getColVal(matchedRow, "Wage Type") || "").trim();
                payType = String(getColVal(matchedRow, "Pay Type") || "").trim();
                expenseAccount = String(getColVal(matchedRow, "Expense Account") || "").trim();
                payableAccount = String(getColVal(matchedRow, "Payable Account") || "").trim();
            } else {
                // Fallback to importPayrollItemsMap if available
                if (rowNum !== null) {
                    const itemsArray = Array.from(importPayrollItemsMap.values());
                    const itemFromMap = itemsArray[rowNum - 1] || itemsArray[rowNum - 2] || itemsArray[rowNum];
                    if (itemFromMap) {
                        itemName = itemFromMap.ItemName || "-";
                        itemType = itemFromMap.ItemType || "-";
                        wageType = itemFromMap.WageType || "";
                        payType = itemFromMap.PayType || "";
                        department = itemFromMap.DeptJobDetails?.[0]?.Department || "-";
                        jobTitle = itemFromMap.DeptJobDetails?.[0]?.JobTitle || "-";
                        expenseAccount = itemFromMap.DeptJobDetails?.[0]?.ExpenseAccount || "";
                        payableAccount = itemFromMap.DeptJobDetails?.[0]?.PayableAccount || "";
                    }
                }

                // Fallback for quoted values: assign to the correct field based on error message context
                if (quoteMatches.length > 0) {
                    const firstVal = quoteMatches[0];
                    if (/payroll\s*item|item\s*name/i.test(line) && itemName === "-") {
                        itemName = firstVal;
                    } else if (/job\s*title/i.test(line) && jobTitle === "-") {
                        jobTitle = firstVal;
                    } else if (/department/i.test(line) && department === "-") {
                        department = firstVal;
                    } else if (/item\s*type/i.test(line) && itemType === "-") {
                        itemType = firstVal;
                    }
                }
            }

            return {
                ...originalRow,
                "Item Type": itemType !== "-" ? itemType : (getColVal(originalRow, "Item Type") || "-"),
                "Item Name": itemName !== "-" ? itemName : (getColVal(originalRow, "Item Name") || "-"),
                "Wage Type": wageType || getColVal(originalRow, "Wage Type") || "",
                "Pay Type": payType || getColVal(originalRow, "Pay Type") || "",
                "Department": department !== "-" ? department : (getColVal(originalRow, "Department") || "-"),
                "Job Title": jobTitle !== "-" ? jobTitle : (getColVal(originalRow, "Job Title") || "-"),
                "Expense Account": expenseAccount || getColVal(originalRow, "Expense Account") || "",
                "Payable Account": payableAccount || getColVal(originalRow, "Payable Account") || "",
                "Error": line.trim()
            };
        });
    };

    // Helper function to extract structured error rows from response
    const getMappedErrors = (data, excelRows = [], importPayrollItemsMap = new Map()) => {
        if (!data || typeof data !== "object") return [];

        if (Array.isArray(data.ErrorRows) && data.ErrorRows.length > 0) {
            if (typeof data.ErrorRows[0] === "string" || !data.ErrorRows[0]["Item Name"]) {
                const rawErrors = data.ErrorRows.map(r => typeof r === "string" ? r : (r.Error || r.Message || JSON.stringify(r)));
                return parseErrorStatus(rawErrors, excelRows, importPayrollItemsMap);
            }
            return data.ErrorRows;
        }

        if (Array.isArray(data.ErrorList) && data.ErrorList.length > 0) {
            if (typeof data.ErrorList[0] === "string" || !data.ErrorList[0]["Item Name"]) {
                const rawErrors = data.ErrorList.map(r => typeof r === "string" ? r : (r.Error || r.Message || JSON.stringify(r)));
                return parseErrorStatus(rawErrors, excelRows, importPayrollItemsMap);
            }
            return data.ErrorList;
        }

        const statusText = data.Status || data.Message || "";
        const isStatusSuccess = typeof statusText === "string" && (
            statusText === "Success" ||
            statusText.toLowerCase().includes("successfully") ||
            statusText.toLowerCase().includes("saved successfully")
        );
        if (typeof statusText === "string" && !isStatusSuccess && statusText.trim() !== "") {
            return parseErrorStatus(statusText, excelRows, importPayrollItemsMap);
        }
        return [];
    };

    // Helper function to calculate imported (success), failed, and total records from file rows & response
    const calculateImportCounts = (data, excelRows = [], mappedErrors = []) => {
        const failedList = getFailedList(data);

        let failedCount = 0;
        if (Array.isArray(failedList)) {
            failedCount = failedList.length;
        } else if (typeof failedList === "number") {
            failedCount = failedList;
        } else if (mappedErrors.length > 0) {
            const uniqueFailedRows = new Set();
            mappedErrors.forEach(err => {
                const rowMatch = String(err.Error || "").match(/\bRow\s+(\d+)\b/i);
                if (rowMatch) {
                    uniqueFailedRows.add(`row-${rowMatch[1]}`);
                } else if (err["Item Name"] && err["Item Name"] !== "-") {
                    uniqueFailedRows.add(`item-${err["Item Name"]}`);
                }
            });
            failedCount = uniqueFailedRows.size > 0 ? uniqueFailedRows.size : mappedErrors.length;
        }

        const totalCount = excelRows.length;
        const successCount = failedCount > 0 ? Math.max(0, totalCount - failedCount) : totalCount;

        return {
            imported: successCount,
            failed: failedCount,
            total: totalCount
        };
    };

    const handleImportClick = async () => {
        const corpIdVal = selectedCorp?.ID || selectedCorp?.value || selectedCorp?.corpValue;
        if (!selectedCorp || !corpIdVal || corpIdVal === "000000000000000000000000000000000000") {
            enqueueSnackbar("Please select a Corporation before importing the file.", { variant: "error" });
            return;
        }

        if (!fileObj) {
            enqueueSnackbar("Please upload a file.", { variant: "error" });
            return;
        }

        setErrorRows([]); // Clear previous errors
        setActionLoading(true);

        let excelRows = [];
        let importPayrollItemsMap = new Map();

        try {
            const arrayBuffer = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsArrayBuffer(fileObj);
                reader.onload = () => resolve(reader.result);
                reader.onerror = (error) => reject(error);
            });

            if (fileObj.size === 0) {
                setAlertPopup({
                    open: true,
                    type: "error",
                    message: "The uploaded file is empty. Please check and upload a valid file.",
                    onConfirm: () => setAlertPopup(prev => ({ ...prev, open: false }))
                });
                setActionLoading(false);
                return;
            }

            const workbook = XLSX.read(arrayBuffer, { type: 'array' });
            if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
                setAlertPopup({
                    open: true,
                    type: "error",
                    message: "The uploaded file is empty. Please check and upload a valid file.",
                    onConfirm: () => setAlertPopup(prev => ({ ...prev, open: false }))
                });
                setActionLoading(false);
                return;
            }

            const worksheet = workbook.Sheets[workbook.SheetNames[0]];
            if (!worksheet || !worksheet['!ref']) {
                setAlertPopup({
                    open: true,
                    type: "error",
                    message: "The uploaded file is empty. Please check and upload a valid file.",
                    onConfirm: () => setAlertPopup(prev => ({ ...prev, open: false }))
                });
                setActionLoading(false);
                return;
            }

            excelRows = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

            const nonEmptyRows = excelRows.filter(r => Object.values(r).some(v => String(v ?? "").trim() !== ""));

            if (excelRows.length === 0 || nonEmptyRows.length === 0) {
                setAlertPopup({
                    open: true,
                    type: "error",
                    message: "The uploaded file is empty. Please check and upload a valid file.",
                    onConfirm: () => setAlertPopup(prev => ({ ...prev, open: false }))
                });
                setActionLoading(false);
                return;
            }

            excelRows = nonEmptyRows;

            if (excelRows.length > 1001) {
                setAlertPopup({
                    open: true,
                    type: "error",
                    message: "The file contains more than 1000 rows. Please limit the import to 1000 lines at a time.",
                    onConfirm: () => setAlertPopup({ ...alertPopup, open: false })
                });
                setActionLoading(false);
                return;
            }

            const expectedHeaders = [
                "Item Type", "Item Name", "Wage Type", "Pay Type",
                "Department", "Job Title", "Expense Account", "Payable Account"
            ];
            const actualHeaders = Object.keys(excelRows[0] || {}).map(h => h.trim().toLowerCase()).filter(h => !h.includes('__empty'));
            const expectedLower = expectedHeaders.map(h => h.toLowerCase());

            if (JSON.stringify(actualHeaders.sort()) !== JSON.stringify(expectedLower.sort())) {
                setAlertPopup({
                    open: true,
                    type: "error",
                    message: "Column headers do not match the template. Please use the correct format.",
                    onConfirm: () => setAlertPopup({ ...alertPopup, open: false })
                });
                setActionLoading(false);
                return;
            }

            excelRows.forEach(row => {
                const itemName = String(getColVal(row, "Item Name")).trim();
                const itemType = String(getColVal(row, "Item Type")).trim();
                const wageType = String(getColVal(row, "Wage Type")).trim();
                const payType = String(getColVal(row, "Pay Type")).trim();

                const key = `${itemName}_${itemType}_${wageType}_${payType}`;

                if (!importPayrollItemsMap.has(key)) {
                    importPayrollItemsMap.set(key, {
                        ItemName: itemName || null,
                        ItemType: itemType || null,
                        WageType: wageType || null,
                        PayType: payType || null,
                        DeptJobDetails: []
                    });
                }

                importPayrollItemsMap.get(key).DeptJobDetails.push({
                    Department: String(getColVal(row, "Department")).trim() || null,
                    JobTitle: String(getColVal(row, "Job Title")).trim() || null,
                    ExpenseAccount: String(getColVal(row, "Expense Account")).trim() || null,
                    PayableAccount: String(getColVal(row, "Payable Account")).trim() || null
                });
            });

            const payload = {
                CorpId: String(selectedCorp.ID || selectedCorp.value || ""),
                ImportPayrollItem: Array.from(importPayrollItemsMap.values())
            };

            const response = await importPayrollSetup(payload);

            const mappedErrors = getMappedErrors(response, excelRows, importPayrollItemsMap);
            const { imported, failed, total } = calculateImportCounts(response, excelRows, mappedErrors);

            const statusText = response?.Status || response?.Message || "";
            const isStatusSuccess = typeof statusText === "string" && (
                statusText === "Success" ||
                statusText.toLowerCase().includes("successfully") ||
                statusText.toLowerCase().includes("saved successfully")
            );

            const isPureSuccess = (response?.StatusCode === 200 || isStatusSuccess) && failed === 0 && mappedErrors.length === 0 && response?.StatusCode !== 400;

            if (isPureSuccess) {
                setAlertPopup({
                    open: true,
                    type: "success",
                    message: `Total Records: ${total}, Imported: ${imported}.`,
                    onConfirm: () => {
                        setAlertPopup(prev => ({ ...prev, open: false }));
                        if (onSuccess) onSuccess();
                        onClose();
                    }
                });
            } else if (mappedErrors.length > 0 || failed > 0) {
                setErrorRows(mappedErrors);
                enqueueSnackbar("Import failed. Please check the error list.", { variant: "error" });
                if (imported > 0 && onSuccess) onSuccess();
            } else {
                enqueueSnackbar("Import failed. Please check the file and try again.", { variant: "error" });
            }
        } catch (err) {
            console.error("Import error:", err);
            const errData = err?.response?.data || {};
            const errMsg = errData?.Status || errData?.Message || err?.message || "";
            const mappedErrors = getMappedErrors(errData, excelRows, importPayrollItemsMap);
            const fallbackErrors = mappedErrors.length > 0 ? mappedErrors : (errMsg ? parseErrorStatus(errMsg, excelRows, importPayrollItemsMap) : []);
            const { imported } = calculateImportCounts(errData, excelRows, fallbackErrors);

            if (fallbackErrors.length > 0) {
                setErrorRows(fallbackErrors);
                enqueueSnackbar("Import failed. Please check the error list.", { variant: "error" });
                if (imported > 0 && onSuccess) onSuccess();
                return;
            }

            enqueueSnackbar("Import failed. Please try again.", { variant: "error" });
        } finally {
            setActionLoading(false);
        }
    };



    return (

        <Dialog
            open={open}
            fullWidth
            maxWidth="lg"
            sx={payrollSetupStyles.importDialog}
        >
            {actionLoading && <Loader loading={actionLoading} />}
            <Toolbar sx={payrollSetupStyles.importToolbar}>
                <Typography
                    sx={payrollSetupStyles.importTitle}
                    component="div"
                    fontWeight={600}
                    fontSize={"16px"}
                >
                    Payroll Setup Import
                </Typography>

                {errorRows.length > 0 && (
                    <Button
                        size="small"
                        variant="outlined"
                        // color="error"
                        disableElevation
                        sx={payrollSetupStyles.exportErrorsBtn}
                        onClick={exportErrorsToCSV}
                    >
                        Export Errors
                    </Button>
                )}
                <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    sx={payrollSetupStyles.importSubmitBtn}
                    onClick={handleImportClick}
                    disabled={isLoading}
                >
                    Import
                </Button>

                <IconButton
                    size="small"
                    onClick={onClose}
                    style={{
                        backgroundColor: "#686868",
                        borderRadius: 2,
                        width: 20,
                        height: 20,
                        marginLeft: 8
                    }}
                    disableRipple
                >
                    <Close sx={payrollSetupStyles.importCloseIcon} />
                </IconButton>
            </Toolbar>

            <DialogContent sx={payrollSetupStyles.importDialogContent}>
                {/* YOUR UI IS UNCHANGED BELOW */}
                <Stack gap={3} height="100%">
                    <Box >
                        <Typography color="text.secondary" fontSize={11} fontWeight={500} mb={0.5}>
                            Corporation <span style={{ color: "red" }}>*</span>
                        </Typography>

                        <Dropdown
                            label=""
                            name="corporation"
                            value={selectedCorp}
                            loading={corpLoading}
                            options={corporations || []}
                            getOptionLabel={(option) => option?.Name || option?.corpName || option?.label || ""}
                            isEqual={(option, value) => {
                                if (!option || !value) return false;
                                const optId = option?.ID || option?.corpValue || option?.value;
                                const valId = value?.ID || value?.corpValue || value?.value;
                                return String(optId).toLowerCase() === String(valId).toLowerCase();
                            }}
                            disabled={isLoading || corpLoading}
                            onChange={(name, newValue) => setSelectedCorp(newValue || null)}
                            sx={{
                                maxWidth: "250px",
                                "& .MuiFilledInput-root": {
                                    maxHeight: 32,
                                    backgroundColor: "white",
                                    "&:hover": { backgroundColor: "white" },
                                    "&.Mui-focused": { backgroundColor: "white" },
                                    "&.Mui-disabled": { backgroundColor: "#f5f5f5" }
                                },
                                "& .MuiFilledInput-input": {
                                    paddingTop: "6px",
                                    paddingBottom: "6px",
                                },
                            }}
                            inputProps={{
                                style: { paddingLeft: 12, paddingRight: 12 },
                            }}
                        />
                    </Box>

                    <Box>
                        <Typography color="text.secondary" fontSize={12} fontWeight={500} mb={0.5}>
                            File Upload <span style={{ color: "red" }}>*</span>
                        </Typography>
                        <TextField
                            helperText="File types Supported :  .xlxs ,.csv ,.xls"
                            size="small"
                            variant="filled"
                            value={selectedFile || "No file selected"}
                            FormHelperTextProps={{
                                sx: {
                                    color: "primary.main",
                                    m: 0,
                                    ml: 1
                                }
                            }}
                            inputProps={{ style: { fontSize: 11 } }}
                            InputProps={{
                                readOnly: true,
                                sx: { paddingRight: 0 },
                                endAdornment: (
                                    <Button
                                        component="label"
                                        size="small"
                                        sx={{
                                            borderTopLeftRadius: 0,
                                            borderBottomLeftRadius: 0,
                                            height: '100%',
                                        }}
                                    >
                                        Browse
                                        <input
                                            type="file"
                                            hidden
                                            accept=".xlsx,.csv,.xls"
                                            onClick={(e) => { e.target.value = null; }}
                                            onChange={handleFileChange}
                                        />
                                    </Button>
                                ),
                            }}
                            sx={{
                                width: "250px",
                                "& .MuiFilledInput-root": {
                                    height: 32,
                                    backgroundColor: "white",
                                    border: "1px solid #ccc",
                                    borderRadius: 1,
                                    "&:hover": { backgroundColor: "white" },
                                    "&.Mui-focused": { backgroundColor: "white" },
                                    paddingRight: 0,
                                    display: "flex",
                                    alignItems: "center",
                                },
                                "& .MuiFilledInput-input": {
                                    padding: "0 12px",
                                },
                                "& .MuiFilledInput-underline:before": { display: "none" },
                                "& .MuiFilledInput-underline:after": { display: "none" },
                            }}
                        />
                    </Box>

                    {errorRows.length > 0 ? (
                        <Box flexGrow={1} overflow="auto" sx={{ minHeight: 0 }}>
                            <Typography color="error" fontSize={15} fontWeight={500} mb={1}>Error List</Typography>
                            <TableContainer sx={{ border: "1px solid #e0e0e0", borderRadius: 1 }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow sx={{ '& .MuiTableCell-root': { backgroundColor: '#f5f5f5', fontWeight: 'bold' } }}>
                                            <TableCell sx={{ fontSize: 12 }}>Item Type</TableCell>
                                            <TableCell sx={{ fontSize: 12 }}>Item Name</TableCell>
                                            <TableCell sx={{ fontSize: 12 }}>Department</TableCell>
                                            <TableCell sx={{ fontSize: 12 }}>Job Title</TableCell>
                                            <TableCell sx={{ fontSize: 12 }}>Error</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {errorRows.map((row, index) => (
                                            <TableRow key={index}>
                                                <TableCell sx={{ fontSize: 11 }}>{row["Item Type"] || "-"}</TableCell>
                                                <TableCell sx={{ fontSize: 11 }}>{row["Item Name"] || "-"}</TableCell>
                                                <TableCell sx={{ fontSize: 11 }}>{row["Department"] || "-"}</TableCell>
                                                <TableCell sx={{ fontSize: 11 }}>{row["Job Title"] || "-"}</TableCell>
                                                <TableCell sx={{ color: 'red', fontSize: 11, whiteSpace: 'pre-line' }}>{row.Error}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    ) : (
                        <Stack gap={2} mt={5}>
                            <Stack direction={"row"} alignItems={"center"} gap={1}>
                                <Info sx={{ color: "primary.main", fontSize: 20 }} />
                                <Typography color="text.secondary" fontSize={12} fontWeight={500}>
                                    Click on the link to download sample template
                                </Typography>
                            </Stack>

                            <Stack direction={"row"} alignItems={"center"} gap={1}>
                                <Description sx={{ color: "primary.main", fontSize: 16 }} />
                                <Link href={SampleTemplateFile} download="Payroll Setup Import Template.xls" fontSize={14} fontWeight={500} color="primary.main" underline="hover">
                                    Payroll Setup Import Template.xlsx
                                </Link>
                            </Stack>
                        </Stack>
                    )}
                </Stack>
            </DialogContent>
            <PayrollsPopup
                open={alertPopup.open}
                popupType={alertPopup.type}
                message={alertPopup.message}
                onConfirm={alertPopup.onConfirm}
                onCancel={() => setAlertPopup({ ...alertPopup, open: false })}
            />
        </Dialog >
    );
};

export default PayrollImport;
